import type { components } from '@connected/api-client/api.generated';
import type { ColumnDef } from '@tanstack/react-table';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { useAppForm } from '@connected/form-fields-v2/hooks/use-app-form';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { File, FileInput } from '@connected/ui/components/file';
import { Popout, PopoutBody, PopoutContent, PopoutHeader, PopoutTitle } from '@connected/ui/components/popout';
import { Skeleton } from '@connected/ui/components/skeleton';
import { Spinner } from '@connected/ui/components/spinner';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import { CloudDownload, PlusIcon, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Breadcrumb } from '@/components/breadcrumb';
import { DataTable } from '@/components/data-table';
import { LoadingPage } from '@/components/loading-page';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';
import { formatFileSize } from '@/lib/utils';
import { showConfirm } from '@/stores/confirmer';

export const Route = createFileRoute(
    '/_auth/dashboard/organizations/$organizationId/settings/measuring-instruments/$measurementDeviceId/calibrations/',
)({
    component: RouteComponent,
});

type MeasurementDeviceCalibrationFileList = components['schemas']['ListElectricalMeasurementDeviceCalibrationFileQueryResult'];
type MeasurementDeviceCalibrationFileItem = NonNullable<MeasurementDeviceCalibrationFileList['Files']>[number];
type FileListRequirements = {
    StartDate: string;
    EndDate: string;
    CertificateNumber: string;
    File: MeasurementDeviceCalibrationFileItem | null;
    CalibrationId: string;
};
type CreateMeasurementDeviceFormValues = Omit<
    components['schemas']['CreateElectricalMeasurementDeviceCalibrationCommand'],
    'StartDate' | 'EndDate'
> & {
    StartDate: Date;
    EndDate: Date;
    File?: File | null;
};

function AddCalibrationRapport({
    isOpen,
    setIsOpen,

}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}) {
    const { t } = useTranslation('dashboard');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [loading, setIsLoading] = useState<boolean>(false);

    const { measurementDeviceId } = Route.useParams();
    const DEFAULT_VALUES: CreateMeasurementDeviceFormValues = {
        ElectricalMeasurementDeviceId: measurementDeviceId || '',
        StartDate: new Date(),
        EndDate: new Date(),
        CertificateNumber: '',
        File: selectedFile,
    };

    const expectedFileTypes = useQuery(portaalQueryOptions.electrical.measurementDevicesCalibrations.create.get);

    const validation = useMemo(() => {
        if (expectedFileTypes.isSuccess && expectedFileTypes.isFetched)
            return expectedFileTypes.data;

        return null;
    }, [expectedFileTypes.isSuccess, expectedFileTypes.isFetched, expectedFileTypes.data]);

    const createCalibration = api.useMutation(
        'post',
        '/api/v2/electrical/measurementdevices/calibrations/create',
        {
            onError: (error) => {
                logger.error(
                    {
                        system: 'createMeasurementCalibration',
                        error,
                    },
                    { apiHttpMethod: 'Post', apiUrl: '/api/v2/electrical/measurementdevices/calibrations/create' },
                );
                toast.error(
                    toast.error(error?.Message ?? t('dashboard.general.messages.error')),
                );
            },
        },
    );

    const createUploadCalibration = api.useMutation(
        'post',
        '/api/v2/electrical/measurementdevices/calibrations/files/create',
        {
            onError: (error) => {
                logger.error(
                    {
                        system: 'CreateElectricalMeasurementDeviceCalibrationFile',
                        error,
                    },
                    { apiHttpMethod: 'Post', apiUrl: '/api/v2/electrical/measurementdevices/calibrations/files/create' },
                );
                toast.error(
                    toast.error(error?.Message ?? t('dashboard.general.messages.error')),
                );
            },
            onMutate: () => setIsLoading(true),
            onSuccess: () => {
                queryClient.invalidateQueries(portaalQueryOptions.measurementDevices.listMeasurementDeviceCalibrationsOptions({ electricalMeasurementDeviceId: measurementDeviceId })).then(() => {
                    setIsOpen(false);
                    toast.success(t('dashboard.settings.measuring-instruments.calibrations.message.createSuccess'));
                });
            },
        },
    );

    const form = useAppForm({
        defaultValues: DEFAULT_VALUES,
        onSubmit: async (values) => {
            const startDate = values.formApi.getFieldValue('StartDate') as Date | undefined;
            const endDate = values.formApi.getFieldValue('EndDate') as Date | undefined;

            const result = await createCalibration.mutateAsync({
                body: {
                    ElectricalMeasurementDeviceId: measurementDeviceId || '',
                    CertificateNumber: values.formApi.getFieldValue('CertificateNumber') || '',
                    StartDate: (startDate)?.toISOString() || '',
                    EndDate: (endDate)?.toISOString() || '',
                },
            });

            const CalibrationId = result.ElectricalMeasurementDeviceCalibrationId;
            await createUploadCalibration.mutateAsync({
                params: {
                    query: {
                        ElectricalMeasurementDeviceCalibrationId: CalibrationId || '',
                        Description: 'description',
                    },
                },
                body: {
                    File: '',
                },
                bodySerializer: () => {
                    const formData = new FormData();
                    formData.append('File', selectedFile as Blob);
                    return formData;
                },
            });
        },

    });

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();

        form.handleSubmit({
            ...DEFAULT_VALUES,
        });
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>{t('dashboard.settings.measuring-instruments.calibrations.create.title')}</PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <form onSubmit={handleSubmit}>
                        <form.AppField
                            name="CertificateNumber"
                            validators={{
                                onChange: validators.createElectricalMeasurementDeviceCalibrationCommand.certificateNumber,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.settings.measuring-instruments.table.certificateNumber')} />
                                    <field.Input
                                        placeholder={t('dashboard.settings.measuring-instruments.table.certificateNumberPlaceholder')}
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />
                        <div className="flex flex-row justify-between">
                            <form.AppField
                                name="StartDate"
                                validators={{
                                    onChange: validators.createElectricalMeasurementDeviceCalibrationCommand.startDate,
                                }}
                                children={field => (
                                    <div className="flex flex-row">
                                        <field.Label>
                                            <field.Label.Title label={t('dashboard.settings.measuring-instruments.table.startDate')} />
                                            <field.Popover closeOnChange={true}>
                                                <field.Popover.Trigger asChild>
                                                    <field.DatePicker.Trigger />
                                                </field.Popover.Trigger>
                                                <field.Popover.Content>
                                                    <field.DatePicker />
                                                </field.Popover.Content>
                                            </field.Popover>
                                            <field.ValidationError />
                                        </field.Label>
                                    </div>
                                )}
                            />
                            <form.AppField
                                name="EndDate"
                                validators={{
                                    onChange: validators.createElectricalMeasurementDeviceCalibrationCommand.endDate,
                                }}
                                children={field => (
                                    <div className="flex gap-4 flex-row">
                                        <div>
                                            <field.Label>
                                                <field.Label.Title label={t('dashboard.settings.measuring-instruments.table.endDate')} />
                                                <field.Popover closeOnChange={true}>
                                                    <field.Popover.Trigger asChild>
                                                        <field.DatePicker.Trigger />
                                                    </field.Popover.Trigger>
                                                    <field.Popover.Content>
                                                        <field.DatePicker />
                                                    </field.Popover.Content>
                                                </field.Popover>
                                                <field.ValidationError />
                                            </field.Label>
                                        </div>
                                    </div>
                                )}
                            />
                        </div>
                        <form.AppField
                            name="File"
                            children={field => (
                                <field.Label asDiv>
                                    <field.Label.Title label={t('dashboard.settings.measuring-instruments.table.calibrateUpload')} />
                                    <div>
                                        <FileInput
                                            maxFileSize={validation?.MaxFileSize}
                                            minFileSize={validation?.MinFileSize}
                                            maxFileName={validation?.MaxFileNameLength}
                                            onChange={(files) => {
                                                setSelectedFile(files[0]);
                                                form.setFieldValue('File', files[0]);
                                            }}
                                            accept="image/jpeg,image/png,image/webp,image/bmp,image/svg+xml,application/pdf"
                                        />
                                        {selectedFile && (
                                            <div className="mt-4">
                                                <File file={selectedFile} />
                                            </div>
                                        )}
                                    </div>
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />
                        <div className="w-full flex flex-row gap-2 mt-5">
                            <Button className="w-full" onClick={() => setIsOpen(false)} variant="outline">{t('dashboard.cancel')}</Button>
                            <Button disabled={loading} type="submit" variant="default" className="w-full">{t('dashboard.save')}</Button>
                        </div>
                    </form>
                </PopoutBody>
            </PopoutContent>
        </Popout>
    );
}

async function DownloadFile(FileId?: string | null, FileName?: string | null) {
    const downloadOptions = portaalQueryOptions.measurementDevices.DownloadElectricalMeasurementDeviceCalibrationFile({
        electricalMeasurementDeviceCalibrationFileId: FileId ?? '',
    });

    const response = await queryClient.ensureQueryData(
        downloadOptions,
    );

    const url = window.URL.createObjectURL(response);
    const link = document.createElement('a');
    link.href = url;
    link.download = FileName || 'download';
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
}

async function DeleteMeasurementCalibration(MeasurementDeviceCalibrationId: string, MeasurementDeviceId: string) {
    const deleteOptions = portaalQueryOptions.measurementDevices.DeleteElectricalMeasurementDeviceCalibrationFile({
        electricalMeasurementDeviceCalibrationId: MeasurementDeviceCalibrationId,
    });

    await queryClient.ensureQueryData(
        deleteOptions,
    ).then(() => {
        queryClient.invalidateQueries(portaalQueryOptions.measurementDevices.listMeasurementDeviceCalibrationsOptions({ electricalMeasurementDeviceId: MeasurementDeviceId }));
    });
}

function RouteComponent() {
    const { t } = useTranslation('dashboard');
    const navigate = useNavigate();
    const { measurementDeviceId, organizationId } = Route.useParams();
    const [isAddCalibrationOpen, setCreateMeasurementOpen] = useState(false);

    const measurementDevicesOptions = useMemo(
        () => portaalQueryOptions.measurementDevices.selectableMeasurementDeviceEditOptions({
            electricalMeasurementDeviceId: measurementDeviceId,
        }),
        [measurementDeviceId],
    );

    const { data, isLoading, isError, error } = useQuery(measurementDevicesOptions);

    const [selectedDownload, setSelectedDownload] = useState({
        FileId: '',
        isDownloading: false,
    });

    const measurementCalibrationsListOptions = useMemo(
        () => portaalQueryOptions.measurementDevices.listMeasurementDeviceCalibrationsOptions({
            electricalMeasurementDeviceId: measurementDeviceId,
        }),
        [measurementDeviceId],
    );

    const calibrations = useQuery(measurementCalibrationsListOptions);

    if (!isLoading && isError) {
        if (isError) {
            logger.error(
                {
                    system: 'getMeasurementDeviceDetails',
                    error,
                },
                { apiHttpMethod: 'Get', apiUrl: '/api/v2/electrical/measurementdevices/edit' },
            );
        }

        toast.error(isError ?? t('dashboard.general.messages.error'));

        navigate({
            to: '/dashboard/organizations/$organizationId/settings/measuring-instruments',
            params: { organizationId },
        });
    }

    const columns = useMemo<ColumnDef<FileListRequirements>[]>(() => ([
        {
            accessorKey: 'Calibrations',
            header: () => (
                <DataTable.Header>
                    {t('dashboard.settings.measuring-instruments.table.calibrationReport')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: FileListRequirements } }) => (
                <DataTable.Cell variant="bold">
                    <DataTable.Cell
                        variant="fileIcon"
                        fileType={
                            row?.original?.File?.File?.Name
                                ? row.original.File.File.Name.split('.').pop() || ''
                                : 'unknown'
                        }
                    />
                    <div>
                        <div>{row?.original?.File?.File?.Name}</div>
                        <div className="font-normal text-text-primary/60 mt-1">
                            {formatFileSize(row?.original?.File?.File?.Size)}
                        </div>
                    </div>
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'CertificateNumber',
            header: () => (
                <DataTable.Header>
                    {t('dashboard.settings.measuring-instruments.table.certificateNumber')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: FileListRequirements } }) => (
                <DataTable.Cell variant="bold">
                    {row?.original?.CertificateNumber}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'CalibrationStartDate',
            header: () => (
                <DataTable.Header>
                    {t('dashboard.settings.measuring-instruments.table.startDate')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: FileListRequirements } }) => (
                <DataTable.Cell variant="bold">
                    {row.original?.StartDate
                        ? format(parseISO(row.original.StartDate), 'dd-MM-yy')
                        : ''}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'CalibrationEndDate',
            header: () => (
                <DataTable.Header>
                    {t('dashboard.settings.measuring-instruments.table.endDate')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: FileListRequirements } }) => (
                <DataTable.Cell variant="bold">
                    {row.original?.EndDate
                        ? format(parseISO(row.original.EndDate), 'dd-MM-yy')
                        : ''}
                </DataTable.Cell>
            ),
        },
        {
            id: 'actionModal',
            meta: {
                sticky: true,
                width: 10,
            },
            header: '',
            cell: ({ row }) => {
                return (
                    <div className="flex gap-1 items-center justify-end ml-auto">
                        <Button
                            variant="secondary"
                            size="icon"
                            className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                            onClick={() => {
                                setSelectedDownload({
                                    FileId: row?.original?.File?.ElectricalMeasurementDeviceCalibrationFileId ?? '',
                                    isDownloading: true,
                                });
                                DownloadFile(
                                    row?.original?.File?.ElectricalMeasurementDeviceCalibrationFileId,
                                    row?.original?.File?.File?.Name,
                                ).then(() => {
                                    setSelectedDownload({
                                        FileId: '',
                                        isDownloading: false,

                                    });
                                });
                            }}

                        >
                            {selectedDownload.FileId === row?.original?.File?.ElectricalMeasurementDeviceCalibrationFileId && selectedDownload.isDownloading ? <Spinner /> : <CloudDownload className="text-text-primary/40 size-5" />}
                        </Button>
                        <Button
                            variant="secondary"
                            size="icon"
                            className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                            onClick={async () => {
                                if (await showConfirm(t('dashboard.settings.measuring-instruments.calibrations.message.confirmDelete'))) {
                                    DeleteMeasurementCalibration(row?.original?.CalibrationId, measurementDeviceId).then(() => {
                                        toast.success(t('dashboard.settings.measuring-instruments.calibrations.message.removeSuccess'));
                                    }).catch((error) => {
                                        toast.error(error ?? t('dashboard.general.messages.error'));
                                    });
                                }
                            }}
                        >
                            <Trash2 className="text-text-primary/40 size-5" />
                        </Button>
                    </div>
                );
            },
        },
    ]), [t, selectedDownload.FileId, selectedDownload.isDownloading, measurementDeviceId]);

    const tableColumns = useMemo(
        () => isLoading
            ? columns.map(column => ({ ...column, cell: () => <Skeleton className="h-8 w-full" /> }))
            : columns,
        [columns, isLoading],
    );

    const tableData = useMemo<FileListRequirements[]>(
        () => {
            return calibrations.data?.Calibrations?.map((item: any) => ({
                ...item,
                File: item?.Files?.[0] || null,
                CalibrationId: item?.ElectricalMeasurementDeviceCalibrationId,
            })) ?? [];
        },
        [calibrations.data],
    );

    if (isLoading) {
        return <LoadingPage />;
    }

    return (
        <div>
            <Breadcrumb
                showHome={false}
                items={[
                    {
                        name: t('dashboard.settings.measuring-instruments.title'),
                        to: '/dashboard/organizations/$organizationId/settings/measuring-instruments',
                        params: { locationId: organizationId },
                    },
                    {
                        isActive: true,
                        name: t('dashboard.settings.measuring-instruments.calibrations.title'),
                    },
                ]}
            />

            <div className="flex flex-col lg:flex-row gap-4 lg:gap-9">
                <div className="w-full">
                    <DataTable.TableHead
                        title={data?.Device?.Name?.toString() ?? ''}
                        subtitle={t('dashboard.settings.measuring-instruments.calibrations.subtitle')}
                        buttons={[
                            { name: t('dashboard.settings.measuring-instruments.calibrations.button'), icon: <PlusIcon />, callback: () => { setCreateMeasurementOpen(true); } },
                        ]}
                    />
                    <DataTable
                        className="rounded-none"
                        columns={tableColumns}
                        data={tableData}
                        pageSize={10}
                    />
                </div>
            </div>
            {
                isAddCalibrationOpen && (
                    <AddCalibrationRapport isOpen={isAddCalibrationOpen} setIsOpen={setCreateMeasurementOpen} />
                )
            }
        </div>
    );
}
